import {
  FC,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';

import { IData } from 'features/ProductConverter';
import { BriefInitialProps } from 'features/ApplicationBrief';
import { BriefProps } from 'utils/types';
import {
  checkIfImageExists,
  cleanString,
  numberFormatter,
  numberReFormatter,
  unMaskInput,
} from 'utils/helpers';
import Button from 'components/Button';
import useGetMetric from 'hooks/useGetMetric';
import useCurrency from 'hooks/useCurreny';
import { Mixpanel } from 'mixpanel';
import useUser from 'api/hooks/useUser';
import withPrintPDF, {
  IWithPrintPDFComponentProps,
} from 'HOC/withPrintPDF';
import { cn } from 'utils/cn';
import useLanguage from 'hooks/useLanguage';

interface Props {
  comparisonData: IData | null;
  product: BriefInitialProps | null;
  brief?: BriefProps[];
  expectedConsumption: number;
  setGeneretedBrief: (val: boolean) => void;
  flowCharts: JSZip.JSZipObject[];
  figures: JSZip.JSZipObject[];
}

const ApplicationBriefResult: FC<Props & IWithPrintPDFComponentProps> = ({
  comparisonData,
  product,
  brief,
  expectedConsumption,
  setGeneretedBrief,
  handlePrint,
  setDocumentTitle,
  isPDF,
  printLoading,
  flowCharts,
  figures,
}) => {
  const { t } = useTranslation();
  const [existsLogo, setExistsLogo] = useState<boolean>(false);
  const { ton } = useGetMetric();
  const { currencyBlue } = useCurrency();
  const { blueLang } = useLanguage();
  const { data: user } = useUser();
  const [imageChart, setImageChart] = useState<null |string>(null);
  const [imageFigure, setImageFigure] = useState<null |string>(null);

  const imageUrl = `/logos/${user?.organization}.png`;

  const getProduct = useMemo(() => {
    if (product?.subIndustry) {
      return brief?.find(
        (briefItem) => cleanString(briefItem.Industry) ===
            cleanString(product?.industry || '') &&
          cleanString(briefItem['Product recommendation']) ===
            cleanString(product?.products || '') &&
          cleanString(briefItem.Application) ===
            cleanString(product?.apps || '') &&
          cleanString(briefItem['Sub-Industry']) ===
            cleanString(
              product?.subIndustry === t('noSubSegment')
                ? ''
                : briefItem['Sub-Industry'],
            ),
      );
    }

    return brief?.find(
      (briefItem) => cleanString(briefItem.Industry) ===
          cleanString(product?.industry || '') &&
        cleanString(briefItem['Product recommendation']) ===
          cleanString(product?.products || '') &&
        cleanString(briefItem.Application) === cleanString(product?.apps || ''),
    );
  }, [comparisonData, product, brief]);

  const annualSavings = useMemo(() => {
    if (!comparisonData?.costDelivered || !expectedConsumption) {
      return 0;
    }

    const omyaSum =
      Number(unMaskInput(comparisonData?.costDelivered)) * expectedConsumption;
    const reformattedAnnualExpenditure = numberReFormatter(
      comparisonData?.annualExpenditure,
    );

    return Number(reformattedAnnualExpenditure) - omyaSum;
  }, [expectedConsumption, comparisonData]);

  const relativeSavings = useMemo(() => {
    if (!annualSavings || !comparisonData?.annualExpenditure) {
      return 0;
    }

    const reformattedAnnualExpenditure = numberReFormatter(
      comparisonData?.annualExpenditure,
    );

    const percent =
      (annualSavings * 100) / Number(reformattedAnnualExpenditure);

    return percent;
  }, [annualSavings, comparisonData]);

  const omyaProductArr = useMemo(
    () => [
      {
        id: 1,
        title: `${t('omyaProduct')}:`,
        value: comparisonData?.omya,
        color: 'black',
      },
      {
        id: 2,
        title: `${t('expectedConsumption')}:`,
        value: expectedConsumption + ` ${ton}/` + t('year'),
        color: 'black',
      },
      {
        id: 3,
        title: `${t('productCost')}:`,
        value:
          numberFormatter(
            Number(unMaskInput(comparisonData?.costDelivered ?? '')) *
              expectedConsumption,
          ) +
          ` ${currencyBlue}/` +
          t('year'),
        color: 'black',
      },
      {
        id: 4,
        title: `${t('savings')}:`,
        value: numberFormatter(annualSavings) + ` ${currencyBlue}/` + t('year'),
        color: annualSavings > 0 ? 'green' : 'orange',
      },
      {
        id: 5,
        title: `${t('relativeSavings')}:`,
        value: Math.round(relativeSavings) + ' %',
        color: 'black',
      },
    ],
    [relativeSavings, annualSavings, comparisonData, expectedConsumption],
  );

  const additionalBenefits = useMemo(() => {
    const result = [];
    getProduct?.['Product benefits1'] &&
      result.push(getProduct?.['Product benefits1']);
    getProduct?.['Product benefits2'] &&
      result.push(getProduct?.['Product benefits2']);
    getProduct?.['Product benefits3'] &&
      result.push(getProduct?.['Product benefits3']);
    getProduct?.['Product benefits4'] &&
      result.push(getProduct?.['Product benefits4']);
    getProduct?.['Product benefits5'] &&
      result.push(getProduct?.['Product benefits5']);
    getProduct?.['Product benefits6'] &&
      result.push(getProduct?.['Product benefits6']);
    getProduct?.['Additional optional benefits'] &&
      result.push(getProduct?.['Additional optional benefits']);
    getProduct?.['Additional optional benefits2'] &&
      result.push(getProduct?.['Additional optional benefits2']);
    getProduct?.['Additional optional benefits3'] &&
      result.push(getProduct?.['Additional optional benefits3']);
    getProduct?.['Additional optional benefits4'] &&
      result.push(getProduct?.['Additional optional benefits4']);
    getProduct?.['Additional optional benefits5'] &&
      result.push(getProduct?.['Additional optional benefits5']);
    getProduct?.['Additional optional benefits6'] &&
      result.push(getProduct?.['Additional optional benefits6']);

    return result;
  }, [getProduct]);

  const getDocumentTitle = () => `Project Brief${
    getProduct?.Application ? ` ${getProduct?.Application}` : ''
  }${
    getProduct?.['Product recommendation']
      ? ` = ${getProduct?.['Product recommendation']}`
      : ''
  }`;

  const getImage = async (data: JSZip.JSZipObject[]) => {
    const product = getProduct?.['Project Brief Code'];

    if (!product) {
      return null; 
    }

    const file = data?.find((i: JSZip.JSZipObject) => i.name.includes(product.toString()));

    if (!file) {
      return null; 
    }

    const blob = await file.async('blob');

    return URL.createObjectURL(blob);
  };

  useEffect(() => {
    if (!isPDF) {
      setDocumentTitle(getDocumentTitle());
    }
  }, [getProduct]);

  useEffect(() => {
    getImage(flowCharts).then((res) => setImageChart(res));
  }, [getProduct, flowCharts]);

  useEffect(() => {
    getImage(figures).then((res) => setImageFigure(res));
  }, [getProduct, figures]);

  useEffect(() => {
    checkIfImageExists(imageUrl, (exists: boolean) => {
      setExistsLogo(exists);
    });
  }, [user]);

  const generatePDF = async () => {
    if (user) {
      Mixpanel.track('Application Brief Donwload Pdf', {
        role: user?.role?.name,
        organization: user?.organization,
      });
    }

    handlePrint();
  };

  if (!isPDF) {
    return (
      <>
        {!isPDF && (
          <div id="content-id" className={cn('grid gap-6 grid-cols-2 sm:grid-cols-1',
            { 'mt-2 px-4 py-2': isPDF },
          )}>
            <div className="flex flex-col">
              <h2 className="text-xl font-bold mb-4">{getDocumentTitle()}</h2>
              {product?.watchCompanyName && (
                <div className="flex items-center border border-primitiveGrey p-8 mb-6 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                  <div
                    className="bg-blue50 mr-6 p-4 rounded-lg w-[100px] h-[50px] flex justify-center items-center"
                    style={{ backgroundColor: '#f0f9ff' }}
                  >
                    {existsLogo && product?.watchCompanyName ? (
                      <img src={imageUrl} alt="logo" />
                    ) : (
                      <div className="font-medium">logo</div>
                    )}
                  </div>
                  <div className="text-xl font-bold">
                    {product?.watchCompanyName}
                  </div>
                </div>
              )}
              <div className="flex-grow border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                <div className="text-xl font-bold mb-6 md:mb-4">
                  {t('productDescription')}
                </div>
                <div className="text-secondaryText">
                  {getProduct?.['Product description']}
                </div>
                <div className="flex itemm-center justify-center mt-6">
                  {getProduct?.['Project Brief Code'] && (
                    <img
                      src={imageFigure || `/figures/${blueLang}/${getProduct?.['Project Brief Code']}.jpg`}
                      alt=""
                    />
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="border border-primitiveGrey p-8 mb-6 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                <div className="text-xl font-bold mb-6 md:mb-4">{t('keyAdv')}</div>
                {additionalBenefits
                  .slice(0, 6)
                  ?.map((benefits: string, i: number) => (
                    <div
                      key={`${benefits}+ ${i}`}
                      className="flex items-center mb-2"
                    >
                      <div
                        style={{
                          width: '6px',
                          height: '6px',
                          marginRight: '0.5rem',
                          backgroundColor: '#007AC1',
                          borderRadius: '6px',
                          flexShrink: '0',
                        }}
                      />
                      {benefits}
                    </div>
                  ))}
              </div>
              <div className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                <div className="text-xl font-bold mb-6 md:mb-4">
                  {t('implementationExample')}
                </div>
                <div className="flex itemm-center justify-center mt-6">
                  {getProduct?.['Project Brief Code'] && (
                    <img src={imageChart || `/flowCharts/${blueLang}/${getProduct?.['Project Brief Code']}.jpg`} alt="Implementation example" />
                  )}
                </div>
              </div>
            </div>

            {!!comparisonData && (
              <div className={cn('border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl break-inside-avoid',
                isPDF ? 'col-span-2' : 'col-span-2 sm:col-span-1 2xl:col-span-1')}>
                <div className="text-xl font-bold mb-6 md:mb-4">
                  {t('productFigures')}
                </div>
                {omyaProductArr.map((omyaProduct) => (
                  <div className="flex" key={omyaProduct.id}>
                    <div className="text-secondaryText mb-2">
                      {omyaProduct.title} &nbsp;
                    </div>
                    {omyaProduct.id === 4 ? (
                      <>
                        {omyaProduct.color === 'orange' ? (
                          <div className={'font-medium text-orange'}>
                            {omyaProduct.value}
                          </div>
                        ) : (
                          <div className={'font-medium text-green'}>
                            {omyaProduct.value}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={'font-medium text-black'}>
                        {omyaProduct.value}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!isPDF && (
          <div className="mt-6 flex justify-center md:flex-col md:items-center">
            <Button
              onClick={() => setGeneretedBrief(false)}
              className="mr-2.5 w-[194px] md:mr-0"
              btnType="secondary"
            >
              {t('back')}
            </Button>

            <Button
              onClick={generatePDF}
              className="w-[194px] md:mt-3"
              disabled={printLoading}
            >
              {t('donwloadPdf')}
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <table className='text-black'>
      <thead>
        <tr>
          <th className='border-none'>
            <div className="flex flex-col">
              <h2 className="text-xl font-bold mb-4 text-black">{getDocumentTitle()}</h2>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className='border-none'>
            <div id="content-id">
              <div className="flex flex-col">
                {product?.watchCompanyName && (
                  <div className="flex items-center border border-primitiveGrey p-8 mb-6 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                    <div
                      className="bg-blue50 mr-6 p-4 rounded-lg w-[100px] h-[50px] flex justify-center items-center"
                      style={{ backgroundColor: '#f0f9ff' }}
                    >
                      {existsLogo && product?.watchCompanyName ? (
                        <img src={imageUrl} alt="logo" />
                      ) : (
                        <div className="font-medium">logo</div>
                      )}
                    </div>
                    <div className="text-xl font-bold">
                      {product?.watchCompanyName}
                    </div>
                  </div>
                )}
                <div className="flex-grow border border-primitiveGrey p-8 mb-6 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                  <div className="text-xl font-bold mb-6 md:mb-4">
                    {t('productDescription')}
                  </div>
                  <div className="text-secondaryText">
                    {getProduct?.['Product description']}
                  </div>
                  <div className="flex itemm-center justify-center mt-6">
                    {getProduct?.['Project Brief Code'] && (
                      <img
                        src={imageFigure || `/figures/${blueLang}/${getProduct?.['Project Brief Code']}.jpg`}
                        alt=""
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className='mb-6'>
                <div className="border border-primitiveGrey p-8 mb-6 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                  <div className="text-xl font-bold mb-6 md:mb-4">{t('keyAdv')}</div>
                  {additionalBenefits
                    .slice(0, 6)
                    ?.map((benefits: string, i: number) => (
                      <div
                        key={`${benefits}+ ${i}`}
                        className="flex items-center mb-2"
                      >
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            marginRight: '0.5rem',
                            backgroundColor: '#007AC1',
                            borderRadius: '6px',
                            flexShrink: '0',
                          }}
                        />
                        {benefits}
                      </div>
                    ))}
                </div>
                <div className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl break-inside-avoid">
                  <div className="text-xl font-bold mb-6 md:mb-4">
                    {t('implementationExample')}
                  </div>
                  <div className="flex itemm-center justify-center mt-6">
                    {getProduct?.['Project Brief Code'] && (
                      <img src={imageChart || `/flowCharts/${blueLang}/${getProduct?.['Project Brief Code']}.jpg`} alt="Implementation example" />
                    )}
                  </div>
                </div>
              </div>

              {!!comparisonData && (
                <div className={cn('border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl break-inside-avoid',
                  isPDF ? 'col-span-2' : 'col-span-2 sm:col-span-1 2xl:col-span-1')}>
                  <div className="text-xl font-bold mb-6 md:mb-4">
                    {t('productFigures')}
                  </div>
                  {omyaProductArr.map((omyaProduct) => (
                    <div className="flex" key={omyaProduct.id}>
                      <div className="text-secondaryText mb-2">
                        {omyaProduct.title} &nbsp;
                      </div>
                      {omyaProduct.id === 4 ? (
                        <>
                          {omyaProduct.color === 'orange' ? (
                            <div className={'font-medium text-orange'}>
                              {omyaProduct.value}
                            </div>
                          ) : (
                            <div className={'font-medium text-green'}>
                              {omyaProduct.value}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={'font-medium text-black'}>
                          {omyaProduct.value}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default withPrintPDF(ApplicationBriefResult);
